import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Info } from 'lucide-react';

const BookReaderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchBook();
  }, [id]);

  const fetchBook = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/books/${id}`);
      const data = await response.json();
      setBook(data);
    } catch (error) {
      console.error('Error fetching book:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f17b21] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading book...</p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Not Found</h2>
          <button
            onClick={() => navigate('/library')}
            className="mt-4 bg-[#f17b21] text-white px-6 py-3 rounded-lg hover:bg-[#d66a1a] transition-colors"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/library')}
                className="flex items-center gap-2 text-gray-600 hover:text-[#f17b21] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-semibold">Back to Library</span>
              </button>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-lg font-bold text-gray-900 line-clamp-1">{book.title}</h1>
                <p className="text-sm text-gray-600">{book.author}</p>
              </div>
            </div>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Info className="w-5 h-5" />
              <span className="hidden md:inline">Book Info</span>
            </button>
          </div>
        </div>
      </div>

      {/* Book Info Sidebar */}
      {showInfo && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-40 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Book Information</h2>
              <button
                onClick={() => setShowInfo(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {book.cover_image && (
              <img
                src={book.cover_image}
                alt={book.title}
                className="w-full rounded-lg mb-4 shadow-md"
              />
            )}

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Title</h3>
                <p className="text-gray-700">{book.title}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Author</h3>
                <p className="text-gray-700">{book.author}</p>
              </div>

              {book.category && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Category</h3>
                  <span className="inline-block px-3 py-1 bg-[#f17b21] text-white text-sm rounded-full">
                    {book.category}
                  </span>
                </div>
              )}

              {book.description && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{book.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
          <object
            data={book.pdf_url}
            type="application/pdf"
            className="w-full h-full"
            style={{ border: 'none' }}
          >
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(book.pdf_url)}&embedded=true`}
              title={book.title}
              className="w-full h-full"
              style={{ border: 'none' }}
            />
          </object>
        </div>
      </div>
    </div>
  );
};

export default BookReaderPage;
